/**
 * edit-diff — turns a graph-op batch + the pre-apply graph into human-readable
 * diff lines for the AI edit card. Pure: no React, no stores, no side effects.
 *
 * Lines describe what the batch changed in the user's language, using node
 * display names from the UniversalNodeRegistry (not raw types) and pre-apply
 * parameter values captured from the graph (for `old → new` rendering).
 */
import { aiGraphView } from '@/features/backtest/utils/ai-graph-view'
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import type { GraphNode } from '@/features/backtest/schemas/backtest-types'
import type { DraftGraph } from '@/features/backtest/graph-engine/types'
import type { GraphOp } from '@/features/backtest/graph-engine/graph-ops'
import { graphToRecipe } from '@/features/backtest/graph-engine/graph-to-recipe'

export interface DiffLine {
  /** Which op produced this line (multiple lines can share a kind). */
  kind: GraphOp['op']
  /** Rendered, user-facing text. */
  text: string
}

/** Resolve a node type to its display name, falling back to the raw type. */
function displayNameOfType(type: string): string {
  return UniversalNodeRegistry.getSchemaResolved(type)?.displayName ?? type
}

/** Normalize descriptor-array or record parameters into a plain record. */
function paramsToRecord(raw: unknown): Record<string, unknown> {
  if (Array.isArray(raw)) {
    const out: Record<string, unknown> = {}
    for (const p of raw) {
      if (p && typeof p === 'object' && 'name' in p) {
        out[(p as { name: string }).name] = (p as { value?: unknown }).value
      }
    }
    return out
  }
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>
  return {}
}

function formatValue(v: unknown): string {
  if (v === undefined || v === null) return '(unset)'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

/** Strip a `.handle` / `.field` suffix from a ref, leaving the step/node id. */
function refHead(ref: string): string {
  const dot = ref.indexOf('.')
  return dot === -1 ? ref : ref.slice(0, dot)
}

/**
 * Resolver over the PRE-APPLY graph. Ops reference recipe step ids (what
 * read_current_graph shows the model) or raw node ids; both resolve here.
 * Nodes the batch adds aren't in the pre-apply graph — their labels come from
 * the op's declared type instead.
 */
class DiffContext {
  private nodeById = new Map<string, GraphNode>()
  private idMap: Map<string, string>
  /** op id → declared type, for nodes created within this batch. */
  addedTypes = new Map<string, string>()

  constructor(graph: DraftGraph) {
    for (const n of graph.nodes) this.nodeById.set(n.id, n)
    // The ids read_current_graph showed the model (backtest/utils/ai-graph-view.ts).
    const view = aiGraphView(graph.nodes, graph.edges)
    this.idMap = graphToRecipe(view.nodes, view.edges).idMap
  }

  /** Resolve a step id or raw node id to a pre-apply node. */
  nodeFor(part: string): GraphNode | undefined {
    const viaMap = this.idMap.get(part)
    if (viaMap && this.nodeById.has(viaMap)) return this.nodeById.get(viaMap)
    return this.nodeById.get(part)
  }

  /** Human label for a ref (step id / node id, with optional `.handle`). */
  label(ref: string): string {
    const head = refHead(ref)
    const node = this.nodeFor(head)
    if (node) return displayNameOfType(node.type)
    const addedType = this.addedTypes.get(head)
    if (addedType) return displayNameOfType(addedType)
    return head
  }
}

/**
 * Build human-readable diff lines for an op batch applied to `preApplyGraph`.
 * One `set_params` op yields one line per changed parameter; every other op
 * yields a single line.
 */
export function buildEditDiff(ops: GraphOp[], preApplyGraph: DraftGraph): DiffLine[] {
  const ctx = new DiffContext(preApplyGraph)
  // Pre-register batch-added node types so later ops referencing them can label.
  for (const op of ops) {
    if (op.op === 'add_node' || op.op === 'insert_between') ctx.addedTypes.set(op.id, op.type)
  }

  const lines: DiffLine[] = []
  for (const op of ops) {
    switch (op.op) {
      case 'add_node':
        lines.push({ kind: op.op, text: `+ ${displayNameOfType(op.type)}` })
        break

      case 'remove_node':
        lines.push({ kind: op.op, text: `− ${ctx.label(op.id)}` })
        break

      case 'set_params': {
        const before = paramsToRecord(ctx.nodeFor(op.id)?.data?.parameters)
        const label = ctx.label(op.id)
        for (const [name, next] of Object.entries(op.params)) {
          const prev = before[name]
          const text =
            prev === undefined
              ? `${label} ${name} set to ${formatValue(next)}`
              : `${label} ${name} ${formatValue(prev)} → ${formatValue(next)}`
          lines.push({ kind: op.op, text })
        }
        break
      }

      case 'connect':
        lines.push({ kind: op.op, text: `${ctx.label(op.from)} → ${ctx.label(op.to)} connected` })
        break

      case 'disconnect': {
        const targetHead = refHead(op.to)
        const targetNode = ctx.nodeFor(targetHead)
        const targetId = targetNode?.id
        const handle = op.to.includes('.') ? op.to.slice(op.to.indexOf('.') + 1) : undefined
        // Recover the source side from the edge feeding this input handle.
        const feedingEdge = preApplyGraph.edges.find(
          (e) => e.target === targetId && (handle === undefined || e.targetHandle === handle),
        )
        const targetLabel = ctx.label(op.to)
        if (feedingEdge) {
          const sourceNode = ctx.nodeFor(feedingEdge.source)
          const sourceLabel = sourceNode ? displayNameOfType(sourceNode.type) : feedingEdge.source
          lines.push({ kind: op.op, text: `${sourceLabel} → ${targetLabel} disconnected` })
        } else {
          lines.push({ kind: op.op, text: `${targetLabel} disconnected` })
        }
        break
      }

      case 'insert_between':
        lines.push({
          kind: op.op,
          text: `+ ${displayNameOfType(op.type)} between ${ctx.label(op.from)} and ${ctx.label(op.to)}`,
        })
        break

      case 'replace_node':
        lines.push({
          kind: op.op,
          text: `${ctx.label(op.id)} → ${displayNameOfType(op.type)}`,
        })
        break
    }
  }
  return lines
}

/**
 * Node ids a batch touched, resolved against the POST-apply idMap (from
 * applyGraphOps). Used to pulse-highlight affected nodes on canvas when the
 * batch lands. Deduplicated, order-preserving.
 */
export function affectedNodeIds(ops: GraphOp[], idMap: Map<string, string>): string[] {
  const resolve = (ref: string): string => {
    const head = refHead(ref)
    return idMap.get(head) ?? head
  }
  const ids = new Set<string>()
  for (const op of ops) {
    switch (op.op) {
      case 'add_node':
      case 'set_params':
      case 'remove_node':
      case 'replace_node':
        ids.add(resolve(op.id))
        break
      case 'connect':
        ids.add(resolve(op.from))
        ids.add(resolve(op.to))
        break
      case 'disconnect':
        ids.add(resolve(op.to))
        break
      case 'insert_between':
        ids.add(resolve(op.id))
        ids.add(resolve(op.from))
        ids.add(resolve(op.to))
        break
    }
  }
  return [...ids]
}
